/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import type { Operation } from 'fast-json-patch';

import { MiddlewareService } from '../middleware';
import { TransportConfig } from './entries';
import { prepareJsonPatchData } from './utils';
import { TransportMiddlewareProps, TransportRequestTypes, TransportStrategies } from './enums';
import { HttpMethods } from '../../enums';

import type { BaseRequestData } from '../../../interfaces';
import type {
    TransportMiddlewareContext,
    TransportMiddlewareRequest,
    TransportMiddlewareResponse,
    TransportPatchData,
    TransportRequestOptions,
    TransportServiceConfig,
} from './interfaces';

type Req<Data> = TransportMiddlewareRequest<Data>;
type Res<SuccessResponse, FailedResponse> = TransportMiddlewareResponse<SuccessResponse, FailedResponse>;

export class TransportService<Fail> {

    readonly config: TransportConfig;

    protected readonly middlewareService: MiddlewareService<TransportMiddlewareContext>;

    constructor(transportServiceConfig: TransportServiceConfig<TransportMiddlewareContext, Req<any>, Res<any, Fail>>) {
        const {
            commands,
            httpRequestService,
            isErrorResponseData,
            ...transportConfigParams
        } = transportServiceConfig;

        this.config = new TransportConfig(transportConfigParams);
        this.middlewareService = new MiddlewareService<TransportMiddlewareContext>({
            responseProp: TransportMiddlewareProps.ResponseProp,
            errorProp: TransportMiddlewareProps.ErrorResponse,
            context: {
                httpRequestService,
                config: this.config,
                commands,
                isErrorResponseData,
            },
            strategies: this.config.getStrategyFlows<any, any, any>(),
            strategyConfigs: this.config.getStrategyConfigs(),
        });
    }

    get<T, R>(url: string, data?: T & BaseRequestData, options?: Partial<TransportRequestOptions>) {
        return this.processRequest<T, R>(url, data, {
            strategy: TransportStrategies.RestServer,
            type: TransportRequestTypes.ToRestServer,
            ...options,
            requestInit: {
                ...options?.requestInit,
                method: HttpMethods.Get,
            },
        });
    }

    post<T, R>(url: string, data?: T & BaseRequestData, options?: Partial<TransportRequestOptions>) {
        return this.processRequest<T, R>(url, data, {
            strategy: TransportStrategies.RestServer,
            type: TransportRequestTypes.ToRestServer,
            ...options,
            requestInit: {
                ...options?.requestInit,
                method: HttpMethods.Post,
            },
        });
    }

    patch<T, R>(url: string, data: Operation[] | TransportPatchData<T>, options?: Partial<TransportRequestOptions>) {
        const { notUseJsonPatch } = options ?? {};
        const patchData: Operation[] = notUseJsonPatch
            ? data as Operation[]
            : prepareJsonPatchData<T>(data as TransportPatchData<T>);

        if (!notUseJsonPatch && patchData && patchData.length === 0) {
            return Promise.resolve({} as R);
        }

        return this.processRequest<Operation[], R>(url, patchData, {
            strategy: TransportStrategies.RestServer,
            type: TransportRequestTypes.ToRestServer,
            ...options,
            requestInit: {
                ...options?.requestInit,
                method: HttpMethods.Patch,
            },
        });
    }

    put<T, R>(url: string, data?: T & BaseRequestData, options?: Partial<TransportRequestOptions>) {
        return this.processRequest<T, R>(url, data, {
            strategy: TransportStrategies.RestServer,
            type: TransportRequestTypes.ToRestServer,
            ...options,
            requestInit: {
                ...options?.requestInit,
                method: HttpMethods.Put,
            },
        });
    }

    delete<T, R>(url: string, data?: T & BaseRequestData, options?: Partial<TransportRequestOptions>) {
        return this.processRequest<T, R>(url, data, {
            strategy: TransportStrategies.RestServer,
            type: TransportRequestTypes.ToRestServer,
            ...options,
            requestInit: {
                ...options?.requestInit,
                method: HttpMethods.Delete,
            },
        });
    }

    request<T, R>(url: string, data?: T & BaseRequestData, options?: Partial<TransportRequestOptions>) {
        return this.processRequest<T, R>(url, data, {
            strategy: TransportStrategies.WebServer,
            type: TransportRequestTypes.ToWebServer,
            ...options,
            requestInit: {
                method: HttpMethods.Post, // Default method should be changeable in general request
                ...options?.requestInit,
            },
        });
    }

    /**
     * @deprecated Use request method
     */
    requestSync<T, R>(
        url: string,
        callback: (resp: R | Fail) => void,
        data?: T & BaseRequestData,
        options?: Partial<TransportRequestOptions>,
    ): void {
        this.request<T, R>(url, data, options)
            .then(resp => callback(resp as R))
            .catch(resp => callback(resp as Fail));
    }

    protected processRequest<T, R>(url: string, data: T & BaseRequestData, options: TransportRequestOptions) {
        const { strategy, strategyConfigs, rerunLimit, allowedAuthAttempts, ...restOptions } = options;
        const initialReq: Req<T> = {
            originalUrl: url,
            originalData: data,
            options: {
                ...restOptions,
            },
            allowedAuthAttempts: allowedAuthAttempts ?? this.config.getAllowedAuthAttempts(), // number of requests when got 401 code
        };

        return this.middlewareService.run<Req<T & BaseRequestData>, Res<R, Fail>, R, Fail>(initialReq, {
            strategy,
            rerunLimit,
            configs: {
                [strategy]: strategyConfigs,
            },
        });
    }

}
